import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createOrganizationAction, editOrganizationAction } from 'modules/investmentAssets/actions';
import { showToast } from 'components/Toast';
import { TOAST_TYPES } from 'components/Toast/ToastTypes';
import { createPaymentLink } from 'modules/sprite/repository';
import useDialog from 'components/Dialog/useDialog';

const DEFAULT_ORGANIZATION = {
  id: '',
  name: '',
  members: {},
};

export function useOrganizationEdit(organization, onSave, onClose) {
  const literals = useSelector(state => state.i18n.literals.investmentAssets);
  const dispatch = useDispatch();

  const { dialog } = useDialog();
  const [loading, setLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState({
    ...DEFAULT_ORGANIZATION,
    ...organization,
    members: organization.members ? Object.entries(organization.members).map(([email, permission]) => ({
      email,
      permission,
      exist: true,
    })) : [],
  });

  const handleMemberChange = (email, field, value) => {
    setOrganizationData(prevData => ({
      ...prevData,
      members: prevData.members.map(member => {
        if (member.email === email) {
          return {
            ...member,
            [field]: value,
          };
        }
        return member;
      }),
    }));
  };

  const handleAddMember = () => {
    setOrganizationData(prevData => ({
      ...prevData,
      members: [
        ...prevData.members,
        {
          email: '',
          firstName: '',
          lastName: '',
          permission: 1,
        },
      ],
    }));
  };

  const handleRemoveMember = (email) => {
    setOrganizationData(prevData => ({
      ...prevData,
      members: prevData.members.filter(member => member.email !== email),
    }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const isEditing = Boolean(organizationData.id);

      let res;
      if (isEditing) {
        res = await dispatch(editOrganizationAction(
          organizationData.id,
          organizationData,
        ));
      } else {
        res = await dispatch(createOrganizationAction(organizationData));
      }

      showToast({
        type: TOAST_TYPES.SUCCESS,
        message: isEditing ? literals.organizationUpdated : literals.organizationCreated,
      });

      onSave();
      onClose();

      if (res.paymentStatus === 'pending') {
        dialog({
          type: 'info',
          title: literals.youAreRedirectingToPayment,
          text: literals.redirectingToPayment,
        });

        createPaymentLink({
          returnUrl: window.location.href,
        }).then(paymentLink => {
          window.open(paymentLink.url, '_self');
        });
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      showToast({
        type: TOAST_TYPES.ERROR,
        message: literals.errorSavingOrganization || 'Error saving organization',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    organizationData,
    setOrganizationData,
    handleMemberChange,
    handleAddMember,
    handleSave,
    handleRemoveMember,
  };
}
